import { HttpClient } from "@/shared/http";

import {
  CredentialRequestApprovalResult,
  CredentialRequestDenialResult,
} from "./CredentialRequestOperations";
import type { CredentialRequestRecord } from "./CredentialRequestRecord";

interface Options {
  abortSignal: AbortSignal;
}

/**
 * Review, approve, and deny pending CLI credential requests as a Cognito-authenticated user.
 * No organization is required. Approval returns a one-time authorization code; the CLI redeems it for the token.
 */
export class CredentialRequestService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  /** Approve a pending request as the signed-in user, creating the authorization code the CLI redeems. */
  public async approve(
    credentialRequestId: string,
    options?: Partial<Options>,
  ): Promise<CredentialRequestApprovalResult> {
    const response = await this.#httpClient.post(
      this.#requestUrl(credentialRequestId, "approve"),
      {
        signal: options?.abortSignal,
        // A request is approved at most once, so a replay after a dropped connection answers 404
        // and hides the code the first attempt was given.
        idempotent: false,
      },
    );
    return response.json<CredentialRequestApprovalResult>();
  }

  /** Deny a pending credential request. */
  public async deny(
    credentialRequestId: string,
    options?: Partial<Options>,
  ): Promise<CredentialRequestDenialResult> {
    const response = await this.#httpClient.post(
      this.#requestUrl(credentialRequestId, "deny"),
      {
        signal: options?.abortSignal,
        // Denying discards the request, so a replay answers 404 and hides the redirect that tells
        // the waiting CLI.
        idempotent: false,
      },
    );
    return response.json<CredentialRequestDenialResult>();
  }

  /**
   * Read pending request metadata without changing it. An unknown, expired, or already decided request
   * answers the same 404.
   */
  public async getPending(
    credentialRequestId: string,
    options?: Partial<Options>,
  ): Promise<CredentialRequestRecord> {
    const response = await this.#httpClient.get(
      this.#requestUrl(credentialRequestId),
      {
        signal: options?.abortSignal,
      },
    );
    return response.json<CredentialRequestRecord>();
  }

  #requestUrl(credentialRequestId: string, action?: "approve" | "deny"): URL {
    const requestPath = `v1/tokens/requests/${encodeURIComponent(credentialRequestId)}`;
    return this.#httpClient.constructUrl(
      action === undefined ? requestPath : `${requestPath}/${action}`,
    );
  }
}
